<script lang="ts">
    import type {ClientIntegrationInfo, IntegrationWithClients} from '$lib/models/integration/integration';
    import {getIntegrationLogoPath} from '$lib/models/integration/getIntegrationLogoPath';
    import IconButton from '$lib/components/common/iconButton/IconButton.svelte';
    import {aliiIntegrationStore} from '$lib/stores/aliiIntegrationStore';
    import {onMount} from 'svelte';
    import {getDrawerStore} from '@skeletonlabs/skeleton';
    import EntitySyncTable from './EntitySyncTable.svelte';
    import SearchBox from '$lib/components/common/searchBox/SearchBox.svelte';

    export let integration: IntegrationWithClients;

  const drawerStore = getDrawerStore();

  let clients: ClientIntegrationInfo[] = [];
  let syncFlags: string[] = []
  let searchValue: string = '';

  onMount(() => {
    clients = aliiIntegrationStore.getClientsForIntegration(integration.integrationTypeName);
    syncFlags = aliiIntegrationStore.getSyncTypesForIntegration(integration.integrationTypeName);
  });

  $: displayName = integration.integrationTypeName;

  $: filteredClients = clients.filter(client => {
    const searchTerm = searchValue.trim();
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const matchesClient = client.clientName.toLowerCase().includes(searchLower) || 
                         client.clientId.toString().includes(searchLower);
    const matchesEntity = client.entities.some(entity => 
      entity.entityName.toLowerCase().includes(searchLower) || 
      entity.entityId.toString().includes(searchLower)
    );
    return matchesClient || matchesEntity;
  });
</script>

<div class="pb-4 px-4">
  <div class="sticky top-0 bg-white z-10 pt-5">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <img
          src={getIntegrationLogoPath(integration.integrationTypeName)}
          alt={displayName}
          class="w-auto h-10 object-contain"
        />
        <div>
          <div class="text-base font-semibold text-gray-900 leading-tight">{displayName}</div>
          <div class="text-sm text-gray-500 leading-none">{integration.clientCount} Clients</div>
        </div>
      </div>
      <IconButton icon="x" onClick={drawerStore.close} />
    </div>

    <hr class="divider mt-5" />
  </div>

  <div class="mt-3 mb-2">
    <SearchBox bind:value={searchValue} isExpandable={false} />
  </div>

  {#if syncFlags.length > 0}
    <details class="p-3 bg-blue-50 border-l-4 border-blue-400 mt-3 rounded">
      <summary class="cursor-pointer text-sm font-medium">
        Integration Sync Types
      </summary>
      <div class="text-gray-700 text-sm mt-2">
        <span class="mb-2">This integration supports the following syncs:</span>
        <ul class="list-disc list-inside pl-3">
          {#each syncFlags as flag}
            <li>{flag}</li>
          {/each}
        </ul>
      </div>
    </details>
  {/if}

  {#if filteredClients.length > 0}
    <div class="my-2">
      <span class="font-light text-sm text-gray-500">Clients</span>
    </div>
    <div class="space-y-2">
      {#each filteredClients as client (client.clientId)}
        {@const hasError = client.entities.some(t => t.lastSyncErrorUtc)}
        <details class="p-3 rounded border-l-4 bg-gray-50" class:border-gray-400={!hasError} class:bg-gray-50={!hasError} class:border-red-400={hasError} class:bg-red-50={hasError}>
          <summary class="cursor-pointer text-sm font-medium space-x-2" class:text-gray-800={!hasError} class:text-red-800={hasError}>
            <span class="font-semibold">{client.clientId} - {client.clientName}</span>
            <span class="text-sm" class:text-gray-500={!hasError} class:text-red-500={hasError}>{client.entities.length} {client.entities.length === 1 ? 'entity' : 'entities'}</span>
            <button
              class="ml-2 px-3 py-1 text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded transition-colors inline-flex items-center gap-1"
              on:click|stopPropagation={() => window.open(client.logsUrl, '_blank')}
            >
              Seq Logs
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
            </button>
          </summary>

          <div class="mt-4 space-y-2">
            {#if client.entities.length === 1}
              {#each client.entities as entity (entity.entityId)}
                <div class="p-2 rounded bg-white">
                  <div class="text-sm font-regular text-gray-800 space-x-2">
                    <span class="font-regular">{entity.entityId} - {entity.entityName}</span>
                    {#if entity.lastSyncRunUtc}
                      <span class="text-xs text-gray-500 mb-2">Last sync run: {new Date(entity.lastSyncRunUtc).toLocaleString()}</span>
                    {/if}
                    {#if entity.lastSyncErrorUtc}
                      <span class="text-xs text-gray-500 mb-2">Last sync error: {new Date(entity.lastSyncErrorUtc).toLocaleString()}</span>
                    {/if}
                  </div>

                  <div class="bg-white pt-2 rounded">
                    <EntitySyncTable syncTypes={entity.syncTypes} />
                  </div>
                </div>
              {/each}
            {:else}
              {#each client.entities as entity (entity.entityId)}
                <details class="p-2 rounded bg-white">
                  <summary class="cursor-pointer text-sm font-regular text-gray-800 space-x-2">
                    <span class="font-regular">{entity.entityId} - {entity.entityName}</span>
                    {#if entity.lastSyncRunUtc}
                      <span class="text-xs text-gray-500 mb-2">Last sync: {new Date(entity.lastSyncRunUtc).toLocaleString()}</span>
                    {/if}
                    {#if entity.lastSyncErrorUtc}
                      <span class="text-xs text-gray-500 mb-2">Last error: {new Date(entity.lastSyncErrorUtc).toLocaleString()}</span>
                    {/if}
                    {#if entity.lastDeleteAbortedUtc}
                      <span class="text-xs text-gray-500 mb-2">Last delete aborted: {new Date(entity.lastDeleteAbortedUtc).toLocaleString()}</span>
                    {/if}
                  </summary>

                  <div class="bg-white pt-2 rounded">
                    <EntitySyncTable syncTypes={entity.syncTypes} />
                  </div>
                </details>
              {/each}
            {/if}
          </div>
        </details>
      {/each}
    </div>
  {:else if searchValue && filteredClients.length === 0}
    <div class="flex flex-col items-center justify-center py-10 text-center">
      <div class="text-gray-600">No Clients Match Your Search</div>
    </div>
  {:else}
    <div class="flex flex-col items-center justify-center py-10 text-center">
      <div class="text-gray-600">No Clients Found</div>
    </div>
  {/if}
</div>
